import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STATUS_OPTIONS = [
  { v: "new", l: "New" },
  { v: "contacted", l: "Contacted" },
  { v: "booked", l: "Booked" },
  { v: "archived", l: "Archived" },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, booked: 0 });
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  const load = async (opts = {}) => {
    const { silent = false } = opts;
    if (!silent) setRefreshing(true);
    try {
      const [meRes, listRes, statsRes] = await Promise.all([
        api.get("/auth/me"),
        api.get("/inquiries"),
        api.get("/stats"),
      ]);
      setMe(meRes.data);
      setItems(listRes.data || []);
      setStats(statsRes.data || stats);
      setLastSync(new Date());
    } catch (err) {
      if (err?.response?.status === 401) {
        nav("/admin/login");
      } else if (!silent) {
        toast.error("Could not load dashboard.");
      }
    } finally {
      setLoading(false);
      if (!silent) setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // Auto-refresh every 30s to pick up newly-submitted inquiries
    const t = setInterval(() => load({ silent: true }), 30000);
    const onVisible = () => {
      if (document.visibilityState === "visible") load({ silent: true });
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/inquiries/${id}`, { status });
      toast.success(`Status updated: ${status}`);
      load();
    } catch {
      toast.error("Failed to update status.");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/inquiries/${id}`);
      toast.success("Deleted.");
      load();
    } catch {
      toast.error("Failed to delete.");
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (_e) {
      // ignore
    }
    window.localStorage.removeItem("pb_token");
    nav("/admin/login");
  };

  const filtered = tab === "all" ? items : items.filter((i) => i.status === tab);

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#0A0A0A]" data-testid="admin-dashboard-page">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#FDFBF7]/80 border-b border-black/10">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#FF5722]" />
            <span className="font-serif text-xl">
              Photo Booth <em className="text-[#FF5722]">Memories</em>
            </span>
            <span className="overline opacity-70 ml-2">Studio</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#0A0A0A]/70 hidden md:inline">{me?.email}</span>
            <button
              onClick={() => load()}
              disabled={refreshing}
              className="text-sm px-4 py-2 rounded-full border border-black/15 hover:bg-[#0A0A0A] hover:text-[#FDFBF7] transition-colors disabled:opacity-60"
              data-testid="admin-refresh"
              title={lastSync ? `Last updated ${lastSync.toLocaleTimeString()}` : "Refresh"}
            >
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
            <ChangePasswordDialog />
            <button
              onClick={logout}
              className="text-sm px-4 py-2 rounded-full border border-black/15 hover:bg-[#0A0A0A] hover:text-[#FDFBF7] transition-colors"
              data-testid="admin-logout"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="overline text-[#0A0A0A]/60">Overview</div>
            <h1 className="font-serif text-5xl md:text-6xl leading-tight">
              The <em className="text-[#FF5722]">pipeline</em>.
            </h1>
          </div>
          <div className="text-xs text-[#0A0A0A]/60" data-testid="last-sync">
            {lastSync ? `Last synced ${lastSync.toLocaleTimeString()} · auto-refreshes every 30s` : "Loading…"}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { l: "Total", v: stats.total, k: "all" },
            { l: "New", v: stats.new, k: "new" },
            { l: "Contacted", v: stats.contacted, k: "contacted" },
            { l: "Booked", v: stats.booked, k: "booked" },
          ].map((s) => (
            <button
              key={s.l}
              onClick={() => setTab(s.k)}
              className={`text-left border rounded-sm p-6 transition-colors ${
                tab === s.k ? "border-[#FF5722] bg-white" : "border-black/10 bg-white hover:border-black/30"
              }`}
              data-testid={`stat-${s.k}`}
            >
              <div className="overline text-[#0A0A0A]/60">{s.l}</div>
              <div className="font-serif text-5xl mt-2">{s.v}</div>
            </button>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
            <TabsTrigger value="new" data-testid="tab-new">New</TabsTrigger>
            <TabsTrigger value="contacted" data-testid="tab-contacted">Contacted</TabsTrigger>
            <TabsTrigger value="booked" data-testid="tab-booked">Booked</TabsTrigger>
            <TabsTrigger value="archived" data-testid="tab-archived">Archived</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="border border-black/10 rounded-sm bg-white overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Package</TableHead>
                <TableHead>Guests</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10 text-[#0A0A0A]/50">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-10 text-[#0A0A0A]/50" data-testid="empty-state">
                    No inquiries yet.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((i) => (
                <TableRow key={i.id} data-testid={`inquiry-row-${i.id}`}>
                  <TableCell className="font-medium">{i.name}</TableCell>
                  <TableCell className="capitalize">{i.event_type}</TableCell>
                  <TableCell>{i.event_date || "—"}</TableCell>
                  <TableCell className="capitalize">{i.package}</TableCell>
                  <TableCell>{i.guest_count || "—"}</TableCell>
                  <TableCell>
                    <div className="text-xs">
                      <div>{i.email}</div>
                      <div className="text-[#0A0A0A]/60">{i.phone || "—"}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Select value={i.status} onValueChange={(v) => updateStatus(i.id, v)}>
                      <SelectTrigger className="w-[140px]" data-testid={`status-select-${i.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((o) => (
                          <SelectItem key={o.v} value={o.v}>
                            {o.l}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="px-3 py-1.5 rounded-full border border-black/15 text-xs hover:bg-black hover:text-white transition-colors"
                          data-testid={`row-actions-${i.id}`}
                        >
                          Actions
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => window.open(`mailto:${i.email}`)}>
                          Email guest
                        </DropdownMenuItem>
                        {i.phone && (
                          <DropdownMenuItem onClick={() => window.open(`tel:${i.phone}`)}>Call</DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => remove(i.id)} className="text-red-600">
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
}

function ChangePasswordDialog() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated. Use it next time you sign in.");
      setOpen(false);
      reset();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not update password.");
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>
        <button
          className="text-sm px-4 py-2 rounded-full border border-black/15 hover:bg-[#0A0A0A] hover:text-[#FDFBF7] transition-colors"
          data-testid="admin-change-password-open"
        >
          Change password
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Change password</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="overline text-[#0A0A0A]/70">Current password</Label>
            <Input
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="mt-2"
              required
              data-testid="cp-current"
            />
          </div>
          <div>
            <Label className="overline text-[#0A0A0A]/70">New password</Label>
            <Input
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="mt-2"
              minLength={6}
              required
              data-testid="cp-new"
            />
            <p className="text-xs text-[#0A0A0A]/50 mt-1">At least 6 characters.</p>
          </div>
          <div>
            <Label className="overline text-[#0A0A0A]/70">Confirm new password</Label>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-2"
              minLength={6}
              required
              data-testid="cp-confirm"
            />
          </div>
          <DialogFooter className="mt-4">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-4 py-2 rounded-full border border-black/15 text-sm hover:bg-black/5 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="px-5 py-2 rounded-full bg-[#0A0A0A] text-[#FDFBF7] text-sm hover:bg-[#FF5722] transition-colors disabled:opacity-60"
              data-testid="cp-submit"
            >
              {busy ? "Updating…" : "Update password"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
